import React, { useEffect, useState } from "react";
import {
  Mail,
  Users,
  Trello,
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
  Eye,
  EyeOff,
  Copy,
  AlertCircle,
  PlayCircle,
  Lock,
  Key,
  Shield,
  Phone,
  AlertTriangle,
} from "lucide-react";
import AppLayout, { PageHeader } from "../components/AppLayout";
import YousignCard from "../components/YousignCard";
import MailboxesCard from "../components/MailboxesCard";
import AgentBdcCard from "../components/AgentBdcCard";
import { api } from "../lib/api";

export default function ConnectionsPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/connections/status");
      setStatus(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Re-fetch on focus (e.g., after Gmail OAuth callback)
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, []);

  // Parse OAuth callback parameters from URL
  const params = new URLSearchParams(window.location.search);
  const gmailOk = params.get("gmail_ok");
  const gmailError = params.get("gmail_error");

  return (
    <AppLayout>
      <PageHeader
        title="Connexions"
        subtitle="Configurez vos accès Gmail et Chelly. L'agent les utilisera automatiquement."
        breadcrumb={[{ label: "Norea", to: "/" }, "Connexions"]}
      />

      {gmailOk && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-status-completBg border border-status-completBorder text-status-completText rounded-md" data-testid="alert-gmail-ok">
          <CheckCircle2 className="w-5 h-5" />
          <span className="text-sm font-medium">Gmail connecté avec succès : {params.get("email")}</span>
        </div>
      )}
      {gmailError && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-status-erreurBg border border-status-erreurBorder text-status-erreurText rounded-md" data-testid="alert-gmail-error">
          <XCircle className="w-5 h-5" />
          <span className="text-sm font-medium">Erreur Gmail : {gmailError}</span>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          <div className="h-48 bg-white border border-norea-border rounded-lg animate-pulse" />
          <div className="h-48 bg-white border border-norea-border rounded-lg animate-pulse" />
        </div>
      ) : (
        <div className="space-y-6">
          <AgentBdcCard />
          <GmailUnifiedCard gmail={status?.gmail} gmailImap={status?.gmail_imap} onChange={load} />
          <ChellyCard data={status?.chelly} onChange={load} />
          <RingoverCard data={status?.ringover} onChange={load} />
          <MailboxesCard />
          <YousignCard data={status?.yousign} onChange={load} />
          {/* Trello disabled — replaced by internal Kanban view inside the dashboard */}
        </div>
      )}
    </AppLayout>
  );
}

function ConnectionHeader({ icon: Icon, title, connected, configured, lastTest, testid }) {
  return (
    <div className="flex items-start justify-between mb-4" data-testid={`${testid}-header`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-norea-surfaceHover flex items-center justify-center text-norea-primary">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-heading text-xl font-medium text-norea-text">{title}</h3>
          <div className="mt-1 flex items-center gap-2 text-xs">
            {connected ? (
              <span className="badge-status badge-complet">
                <CheckCircle2 className="w-3 h-3" /> Connecté
              </span>
            ) : configured ? (
              <span className="badge-status badge-incomplet">
                <AlertCircle className="w-3 h-3" /> Configuré, à autoriser
              </span>
            ) : (
              <span className="badge-status badge-processing">
                <XCircle className="w-3 h-3" /> Non configuré
              </span>
            )}
            {lastTest && (
              <span className="text-norea-textMuted">
                Dernier test : {new Date(lastTest).toLocaleString("fr-FR")}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============== GMAIL UNIFIED (IMAP + OAuth tabs) ==============
function GmailUnifiedCard({ gmail, gmailImap, onChange }) {
  const defaultTab = gmailImap?.connected ? "imap" : gmail?.connected ? "oauth" : "imap";
  const [tab, setTab] = useState(defaultTab);

  return (
    <div className="card-flat p-6" data-testid="card-gmail-unified">
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-norea-surfaceHover flex items-center justify-center text-norea-primary">
            <Mail className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-heading text-xl font-medium text-norea-text">Gmail</h3>
            <p className="text-xs text-norea-textMuted mt-0.5">
              Choisissez le mode de connexion. <span className="text-norea-text font-medium">IMAP recommandé</span> — pas d'expiration.
            </p>
          </div>
        </div>
      </div>

      {/* Health banner — ALWAYS visible when OVERQUOTA, regardless of active tab */}
      <div className="mt-4">
        <ImapHealthBanner health={gmailImap?.health} onReset={onChange} />
      </div>

      {/* Tabs */}
      <div className="mt-4 mb-4 inline-flex items-center gap-0.5 bg-norea-bg border border-norea-border rounded-md p-0.5" data-testid="gmail-tabs">
        <button
          onClick={() => setTab("imap")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition ${
            tab === "imap" ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"
          }`}
          data-testid="gmail-tab-imap"
        >
          <Shield className="w-3.5 h-3.5" />
          IMAP — permanent
          {gmailImap?.connected && <CheckCircle2 className="w-3 h-3" />}
        </button>
        <button
          onClick={() => setTab("oauth")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition ${
            tab === "oauth" ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"
          }`}
          data-testid="gmail-tab-oauth"
        >
          <Key className="w-3.5 h-3.5" />
          OAuth (7 jours)
          {gmail?.connected && <CheckCircle2 className="w-3 h-3" />}
        </button>
      </div>

      {tab === "imap" ? (
        <GmailImapPanel data={gmailImap} onChange={onChange} />
      ) : (
        <GmailOauthPanel data={gmail} onChange={onChange} />
      )}
    </div>
  );
}

function ImapHealthBanner({ health, onReset }) {
  const [countdown, setCountdown] = React.useState("");
  const [resetting, setResetting] = React.useState(false);

  React.useEffect(() => {
    if (!health?.in_cooldown || !health?.cooldown_until) return;
    const update = () => {
      const until = new Date(health.cooldown_until).getTime();
      const ms = until - Date.now();
      if (ms <= 0) {
        setCountdown("expire…");
        return;
      }
      const m = Math.floor(ms / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setCountdown(`${m}m ${s.toString().padStart(2, "0")}s`);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [health?.in_cooldown, health?.cooldown_until]);

  const retryNow = async () => {
    setResetting(true);
    try {
      await api.post("/connections/gmail-imap/reset-cooldown");
      onReset?.();
    } finally {
      setResetting(false);
    }
  };

  if (!health?.in_cooldown) return null;

  return (
    <div
      className="p-4 rounded-md border border-amber-300 bg-amber-50 text-amber-900"
      data-testid="imap-cooldown-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm">
          <div className="font-semibold mb-1">
            Gmail limite temporairement l'accès IMAP à ce compte
          </div>
          <div className="text-xs mb-3 leading-relaxed">
            Google renvoie <code className="font-mono bg-white/60 px-1 rounded">OVERQUOTA · exceeded command or bandwidth limits</code>. Ce n'est pas une déconnexion : le compte est juste <strong>throttlé côté Google</strong> pour trop de requêtes IMAP sur une courte période. Les polls sont mis en pause automatiquement pour <strong>ne pas prolonger le blocage</strong> (prochaine tentative dans <span className="font-mono font-semibold">{countdown || "…"}</span>).
          </div>

          {health?.trip_count > 0 && (
            <div className="text-xs mb-3 p-2.5 bg-white/70 border border-amber-200 rounded">
              <div className="font-semibold mb-1">
                🔁 Backoff exponentiel actif — niveau {health.trip_count}/{(health.backoff_ladder_hours || [1,2,4,8,24]).length}
              </div>
              <div className="text-[11px]">
                Ladder : {(health.backoff_ladder_hours || [1,2,4,8,24]).map((h,i) => (
                  <span key={i} className={i < health.trip_count ? "font-semibold text-amber-900" : "opacity-50"}>
                    {h}h{i < (health.backoff_ladder_hours || []).length - 1 ? " → " : ""}
                  </span>
                ))} (plafond)
              </div>
              <div className="text-[11px] mt-1 opacity-80">
                Chaque échec consécutif double la pause pour laisser Google le temps de libérer le compte. Le compteur revient à 0 dès qu'un poll réussit ou que tu cliques "Réessayer".
              </div>
            </div>
          )}

          <div className="text-xs mb-3 p-2.5 bg-white/60 border border-amber-200 rounded">
            <div className="font-semibold mb-1">✅ Ce qu'on a corrigé (déjà actif)</div>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Les 2 polls (dossiers + indexation emails) sont désormais <strong>fusionnés en un seul</strong></li>
              <li>Fréquence réduite de 2 min → <strong>5 min</strong> (−66 % de requêtes IMAP/h)</li>
              <li>Backoff exponentiel 1h → 2h → 4h → 8h → 24h à chaque échec OVERQUOTA</li>
              <li>Le bouton "Tester" est bloqué pendant la pause pour ne pas prolonger le ban</li>
            </ul>
          </div>

          <div className="text-xs mb-3 leading-relaxed">
            <strong>Ce qu'il faut faire côté Google :</strong>
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              <li>
                <strong>Attendre.</strong> Google lève le throttling <strong>automatiquement en 1 à 24h</strong> maintenant que les polls sont espacés. Il n'y a plus de page de déblocage manuel (<a href="https://accounts.google.com/DisplayUnlockCaptcha" target="_blank" rel="noreferrer" className="underline opacity-70">l'ancien lien "DisplayUnlockCaptcha"</a> a été supprimé par Google).
              </li>
              <li>
                <strong>Astuce qui aide souvent :</strong> connecte-toi à <a href="https://mail.google.com" target="_blank" rel="noreferrer" className="underline font-medium">mail.google.com</a> avec <code className="font-mono bg-white/60 px-1 rounded">administration.photovoltaique@norea.pro</code> depuis ton navigateur habituel → ça rafraîchit la session côté Google et peut accélérer le déblocage.
              </li>
              <li>
                <strong>Si ça persiste &gt; 24h</strong> : révoque le mot de passe d'application actuel sur <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline font-medium">myaccount.google.com/apppasswords</a>, génère-en un nouveau, mets-le à jour ci-dessous et relance.
              </li>
            </ul>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={retryNow}
              disabled={resetting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium disabled:opacity-60"
              data-testid="btn-reset-imap-cooldown"
              title="Tente une connexion maintenant. Si Google bloque toujours, la pause 1h sera réactivée automatiquement."
            >
              {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
              Réessayer maintenant
            </button>
            <a
              href="https://mail.google.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 text-xs font-medium"
            >
              <ExternalLink className="w-3 h-3" /> Ouvrir Gmail (web)
            </a>
            <a
              href="https://myaccount.google.com/apppasswords"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 text-xs font-medium"
            >
              <ExternalLink className="w-3 h-3" /> Mots de passe d'application
            </a>
          </div>
          {health?.last_error && (
            <div className="mt-3 text-[10px] font-mono text-amber-900/70 bg-white/60 p-1.5 rounded border border-amber-200">
              {health.last_error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GmailImapPanel({ data, onChange }) {
  const [email, setEmail] = useState(data?.email_address || "");
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [label, setLabel] = useState(data?.label_to_process || "1/ Dossiers à traiter");
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showHelp, setShowHelp] = useState(!data?.configured);

  const save = async () => {
    // Pre-flight: warn if the password doesn't look like a Google App Password
    // (16 lowercase letters, optionally with spaces every 4 chars: "abcd efgh ijkl mnop").
    // Google IMAP rejects the regular account password since 2022, even though it works in a browser.
    const trimmed = pwd.trim();
    if (trimmed && trimmed !== "__keep__") {
      const stripped = trimmed.replace(/\s+/g, "");
      const looksLikeAppPassword = /^[a-z]{16}$/.test(stripped);
      if (!looksLikeAppPassword) {
        const proceed = window.confirm(
          "⚠ Ce mot de passe ne ressemble pas à un mot de passe d'application Google.\n\n" +
          "Gmail IMAP N'ACCEPTE PAS votre mot de passe Gmail habituel, même s'il fonctionne dans le navigateur.\n\n" +
          "Un mot de passe d'application ressemble à : abcd efgh ijkl mnop (16 lettres minuscules).\n\n" +
          "Générez-en un sur : https://myaccount.google.com/apppasswords\n\n" +
          "Continuer quand même avec le mot de passe saisi ?"
        );
        if (!proceed) {
          setShowHelp(true);
          return;
        }
      }
    }
    setBusy(true);
    try {
      await api.post("/connections/gmail-imap/save", {
        email_address: email.trim(),
        app_password: trimmed || "__keep__",
        label_to_process: label.trim() || "1/ Dossiers à traiter",
      });
      setPwd("");
      onChange();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data: r } = await api.post("/connections/gmail-imap/test");
      setTestResult(r);
      // If test failed with an auth hint, auto-expand the help section
      if (!r.ok && (r.hint === "app_password_required" || (r.error || "").toLowerCase().includes("identifiants refusés"))) {
        setShowHelp(true);
      }
      if (r.ok) onChange();
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Déconnecter IMAP ?")) return;
    await api.post("/connections/gmail-imap/disconnect");
    onChange();
  };

  return (
    <div data-testid="panel-gmail-imap">
      <div className="mb-3 flex items-center gap-2">
        {data?.connected ? (
          <span className="badge-status badge-complet">
            <CheckCircle2 className="w-3 h-3" /> Connecté · {data.email_address}
          </span>
        ) : (
          <span className="badge-status badge-processing">
            <XCircle className="w-3 h-3" /> Non connecté
          </span>
        )}
        <span className="text-[11px] text-norea-textMuted">Aucune expiration · Auth via App Password</span>
      </div>

      <button
        onClick={() => setShowHelp(!showHelp)}
        className="text-xs text-norea-accent hover:text-norea-primary mb-3"
        data-testid="btn-toggle-imap-help"
      >
        {showHelp ? "▼" : "▶"} Comment générer un mot de passe d'application Google ?
      </button>
      {showHelp && (
        <div className="mb-4 p-4 bg-norea-bg border border-norea-border rounded-md text-xs space-y-1.5 text-norea-textMuted">
          <p className="font-medium text-norea-text mb-2">📋 5 étapes (3 minutes) :</p>
          <ol className="list-decimal list-inside space-y-1.5">
            <li>
              Activez la <strong>double authentification</strong> sur votre compte Google si ce n'est pas déjà fait :
              <a href="https://myaccount.google.com/signinoptions/two-step-verification" target="_blank" rel="noreferrer" className="text-norea-accent underline ml-1">
                myaccount.google.com/signinoptions/two-step-verification
              </a>
            </li>
            <li>
              Ouvrez <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-norea-accent underline">myaccount.google.com/apppasswords</a> (avec le compte qui reçoit les dossiers)
            </li>
            <li>Tapez un nom : <code className="bg-white px-1 py-0.5 rounded border border-norea-border">Norea Platform</code> → cliquez "Créer"</li>
            <li>Google affiche un <strong>mot de passe de 16 caractères</strong> (ex: <code className="font-mono">abcd efgh ijkl mnop</code>). Copiez-le.</li>
            <li>Collez-le ci-dessous (les espaces seront retirés automatiquement) + votre email. Cliquez Sauvegarder puis Tester.</li>
          </ol>
          <p className="text-[11px] mt-2 italic">Ce mot de passe n'expire jamais. Vous pouvez le révoquer à tout moment depuis la même page Google.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="overline block mb-1.5">Email Gmail</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-field font-mono text-xs"
            placeholder="administration.photovoltaique@norea.pro"
            data-testid="imap-email-input"
          />
        </div>
        <div>
          <label className="overline block mb-1.5">
            Mot de passe d'application {data?.app_password_masked && <span className="ml-2 text-norea-textDisabled normal-case tracking-normal">(actuel: {data.app_password_masked})</span>}
          </label>
          <div className="relative">
            <input
              type={showPwd ? "text" : "password"}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="input-field font-mono text-xs pr-10"
              placeholder={data?.configured ? "•••• •••• •••• •••• (laisser vide)" : "abcd efgh ijkl mnop"}
              data-testid="imap-password-input"
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => setShowPwd(!showPwd)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-norea-textMuted hover:text-norea-text"
              tabIndex={-1}
            >
              {showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        <div>
          <label className="overline block mb-1.5">Label à surveiller</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="input-field text-xs"
            placeholder="1/ Dossiers à traiter"
            data-testid="imap-label-input"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={busy || !email} className="btn-secondary" data-testid="btn-imap-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
          Sauvegarder
        </button>
        {data?.configured && (
          <button
            onClick={test}
            disabled={busy || data?.health?.in_cooldown}
            className="btn-primary"
            data-testid="btn-imap-test"
            title={data?.health?.in_cooldown ? "Pause OVERQUOTA active — tester maintenant prolongerait le blocage Google" : ""}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            {data?.health?.in_cooldown ? "Tester (en pause OVERQUOTA)" : "Tester la connexion"}
          </button>
        )}
        {data?.connected && (
          <button onClick={disconnect} className="btn-ghost text-status-erreurText" data-testid="btn-imap-disconnect">
            Déconnecter
          </button>
        )}
      </div>

      {testResult && (
        <div
          className={`mt-3 px-3 py-2 rounded-md text-sm ${
            testResult.ok
              ? "bg-status-completBg border border-status-completBorder text-status-completText"
              : "bg-status-erreurBg border border-status-erreurBorder text-status-erreurText"
          }`}
          data-testid="imap-test-result"
        >
          {testResult.ok ? `✓ ${testResult.message}` : `✗ ${testResult.error}`}
          {!testResult.ok && testResult.hint === "app_password_required" && (
            <div className="mt-2 pt-2 border-t border-status-erreurBorder/40">
              <a
                href="https://myaccount.google.com/apppasswords"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-status-erreurText underline hover:no-underline"
                data-testid="link-generate-app-password"
              >
                🔑 Générer un mot de passe d'application Google →
              </a>
            </div>
          )}
          {testResult.ok && testResult.total_messages !== undefined && (
            <div className="text-xs mt-1 opacity-80">{testResult.total_messages} message(s) dans INBOX</div>
          )}
          {testResult.ok && Array.isArray(testResult.labels) && testResult.labels.length > 0 && (
            <div className="mt-3 pt-3 border-t border-status-completBorder/40">
              <div className="text-xs font-semibold mb-2 text-status-completText">
                📁 Labels disponibles ({testResult.labels.length}) — cliquez pour utiliser :
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto" data-testid="imap-labels-list">
                {testResult.labels.map((lbl) => {
                  const isSelected = lbl === label;
                  const isCurrent = lbl === testResult.label;
                  return (
                    <button
                      key={lbl}
                      type="button"
                      onClick={() => setLabel(lbl)}
                      className={`text-[11px] px-2 py-1 rounded border font-mono transition ${
                        isSelected
                          ? "bg-norea-primary text-white border-norea-primary"
                          : isCurrent && !testResult.label_found
                          ? "bg-white border-status-incompletBorder text-status-incompletText hover:border-norea-primary"
                          : "bg-white border-norea-border text-norea-text hover:border-norea-primary"
                      }`}
                      data-testid={`imap-label-pick-${lbl.replace(/\s+/g, "-")}`}
                      title="Cliquez pour utiliser ce label, puis Sauvegarder"
                    >
                      {lbl}
                      {isSelected && " ✓"}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] mt-2 text-status-completText/80 italic">
                Astuce : cliquez sur un label, puis « Sauvegarder » pour l'enregistrer.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Inline OAuth panel (was GmailCard's body)
function GmailOauthPanel({ data, onChange }) {
  const [clientId, setClientId] = useState(data?.client_id || "");
  const [clientSecret, setClientSecret] = useState("");
  const [showHelp, setShowHelp] = useState(!data?.configured);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const save = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/connections/gmail/save", {
        client_id: clientId.trim(),
        client_secret: clientSecret.trim() || "__keep__",
      });
      setClientSecret("");
      alert(
        `✅ Sauvegardé.\n\nIMPORTANT : copiez cette URI de redirection dans votre OAuth Client Google :\n\n${r.redirect_uri_to_authorize_in_google_cloud}`
      );
      onChange();
    } finally {
      setBusy(false);
    }
  };

  const authorize = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.get("/connections/gmail/authorize");
      window.location.href = r.authorization_url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data: r } = await api.post("/connections/gmail/test");
      setTestResult(r);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Déconnecter Gmail OAuth ?")) return;
    await api.post("/connections/gmail/disconnect");
    onChange();
  };

  return (
    <div data-testid="panel-gmail-oauth">
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        {data?.connected ? (
          <span className="badge-status badge-complet">
            <CheckCircle2 className="w-3 h-3" /> Connecté · {data.email_address}
          </span>
        ) : data?.configured ? (
          <span className="badge-status badge-incomplet">
            <AlertCircle className="w-3 h-3" /> Configuré, à autoriser
          </span>
        ) : (
          <span className="badge-status badge-processing">
            <XCircle className="w-3 h-3" /> Non configuré
          </span>
        )}
        {data?.connected && data?.days_remaining !== null && data?.days_remaining !== undefined && (
          <span className={`text-[11px] px-2 py-0.5 rounded border ${
            data.expiry_critical
              ? "bg-status-erreurBg border-status-erreurBorder text-status-erreurText font-semibold"
              : data.expiry_warning
              ? "bg-status-incompletBg border-status-incompletBorder text-status-incompletText font-medium"
              : "bg-norea-surfaceHover border-norea-border text-norea-textMuted"
          }`} data-testid="oauth-expiry-badge">
            ⏳ Token expire dans {data.days_remaining}j
          </span>
        )}
      </div>

      <div className="mb-3 px-3 py-2 bg-status-incompletBg border border-status-incompletBorder text-status-incompletText text-xs rounded">
        ⚠ En mode Test, Google force une réautorisation tous les 7 jours. Préférez l'onglet <strong>IMAP</strong> pour une connexion permanente.
      </div>

      <button onClick={() => setShowHelp(!showHelp)} className="text-xs text-norea-accent hover:text-norea-primary mb-3" data-testid="btn-toggle-gmail-help">
        {showHelp ? "▼" : "▶"} Guide OAuth (5 min)
      </button>
      {showHelp && (
        <div className="mb-4 p-4 bg-norea-bg border border-norea-border rounded-md text-sm space-y-2 text-norea-text">
          <ol className="list-decimal list-inside space-y-1.5 text-xs text-norea-textMuted">
            <li>Ouvrez <a href="https://console.cloud.google.com/" target="_blank" rel="noreferrer" className="text-norea-accent underline">Google Cloud Console</a> et créez un projet.</li>
            <li>Activez <strong>Gmail API</strong>.</li>
            <li>Créez un OAuth Client (type "Web").</li>
            <li>Ajoutez en redirect URI :
              <div className="mt-1 flex items-center gap-2">
                <code className="text-[10px] bg-white border border-norea-border px-2 py-1 rounded break-all flex-1">
                  {process.env.REACT_APP_BACKEND_URL}/api/connections/gmail/callback
                </code>
                <button onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/connections/gmail/callback`); alert("✓ Copié"); }} className="btn-ghost" data-testid="btn-copy-redirect-uri">
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </li>
            <li>Ajoutez votre email dans <strong>Test users</strong> sur l'écran de consentement OAuth.</li>
          </ol>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="overline block mb-1.5">Client ID</label>
          <input type="text" value={clientId} onChange={(e) => setClientId(e.target.value)} className="input-field font-mono text-xs" placeholder="...apps.googleusercontent.com" data-testid="gmail-client-id-input" />
        </div>
        <div>
          <label className="overline block mb-1.5">
            Client Secret {data?.client_secret_masked && <span className="ml-2 text-norea-textDisabled normal-case tracking-normal">({data.client_secret_masked})</span>}
          </label>
          <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} className="input-field font-mono text-xs" placeholder={data?.configured ? "•••••••• (laisser vide)" : "GOCSPX-..."} data-testid="gmail-client-secret-input" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={busy || !clientId} className="btn-secondary" data-testid="btn-gmail-save">Sauvegarder</button>
        {data?.configured && (
          <button onClick={authorize} disabled={busy} className="btn-primary" data-testid="btn-gmail-authorize">
            {data?.connected ? "Re-autoriser" : "Autoriser Gmail"}
            <ExternalLink className="w-4 h-4" />
          </button>
        )}
        {data?.connected && (
          <>
            <button onClick={test} disabled={busy} className="btn-secondary" data-testid="btn-gmail-test">
              <PlayCircle className="w-4 h-4" />
              Tester
            </button>
            <button onClick={disconnect} className="btn-ghost text-status-erreurText" data-testid="btn-gmail-disconnect">Déconnecter</button>
          </>
        )}
      </div>

      {testResult && (
        <div className={`mt-3 px-3 py-2 rounded-md text-sm ${testResult.ok ? "bg-status-completBg border border-status-completBorder text-status-completText" : "bg-status-erreurBg border border-status-erreurBorder text-status-erreurText"}`} data-testid="gmail-test-result">
          {testResult.ok ? `✓ ${testResult.email} (${testResult.total_messages} messages)` : `✗ ${testResult.error}`}
        </div>
      )}
    </div>
  );
}


// ============== GMAIL ==============
function GmailCard({ data, onChange }) {
  const [clientId, setClientId] = useState(data?.client_id || "");
  const [clientSecret, setClientSecret] = useState("");
  const [showHelp, setShowHelp] = useState(!data?.configured);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const save = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/connections/gmail/save", {
        client_id: clientId.trim(),
        client_secret: clientSecret.trim() || "__keep__",
      });
      setClientSecret("");
      alert(
        `✅ Sauvegardé.\n\nIMPORTANT : copiez cette URI de redirection dans votre OAuth Client Google :\n\n${r.redirect_uri_to_authorize_in_google_cloud}`
      );
      onChange();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const authorize = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.get("/connections/gmail/authorize");
      window.location.href = r.authorization_url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data: r } = await api.post("/connections/gmail/test");
      setTestResult(r);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Déconnecter Gmail ? Vous devrez ré-autoriser ensuite.")) return;
    await api.post("/connections/gmail/disconnect");
    onChange();
  };

  return (
    <div className="card-flat p-6" data-testid="card-gmail">
      <ConnectionHeader
        icon={Mail}
        title="Gmail"
        connected={data?.connected}
        configured={data?.configured}
        lastTest={data?.last_test_at}
        testid="gmail"
      />

      {data?.connected && data?.email_address && (
        <div className="mb-4 px-3 py-2 bg-status-completBg border border-status-completBorder rounded-md text-sm text-status-completText" data-testid="gmail-connected-email">
          📬 Compte connecté : <span className="font-medium">{data.email_address}</span>
        </div>
      )}

      {/* Guide */}
      <button
        onClick={() => setShowHelp(!showHelp)}
        className="text-xs text-norea-accent hover:text-norea-primary mb-3"
        data-testid="btn-toggle-gmail-help"
      >
        {showHelp ? "▼" : "▶"} Guide pas-à-pas pour obtenir vos identifiants Gmail
      </button>
      {showHelp && (
        <div className="mb-4 p-4 bg-norea-bg border border-norea-border rounded-md text-sm space-y-2 text-norea-text">
          <p className="font-medium">📋 Suivez ces 5 étapes (5 min) :</p>
          <ol className="list-decimal list-inside space-y-1.5 text-xs text-norea-textMuted">
            <li>
              Ouvrez{" "}
              <a href="https://console.cloud.google.com/" target="_blank" rel="noreferrer" className="text-norea-accent underline">
                Google Cloud Console
              </a>{" "}
              et créez un projet (ex: "Norea Platform").
            </li>
            <li>
              Dans "APIs &amp; Services" → "Bibliothèque", activez <span className="font-medium">Gmail API</span>.
            </li>
            <li>
              Allez dans "Identifiants" → "Créer des identifiants" → <span className="font-medium">"ID client OAuth"</span> → Type "Application Web".
            </li>
            <li>
              Dans "URI de redirection autorisés", ajoutez exactement :
              <div className="mt-1 flex items-center gap-2">
                <code className="text-[10px] bg-white border border-norea-border px-2 py-1 rounded break-all flex-1">
                  {process.env.REACT_APP_BACKEND_URL}/api/connections/gmail/callback
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/connections/gmail/callback`);
                    alert("✓ Copié");
                  }}
                  className="btn-ghost"
                  data-testid="btn-copy-redirect-uri"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </li>
            <li>
              Copiez le <span className="font-medium">Client ID</span> et le <span className="font-medium">Client Secret</span> ci-dessous.
            </li>
          </ol>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="overline block mb-1.5">Client ID</label>
          <input
            type="text"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="input-field font-mono text-xs"
            placeholder="123456789-xxxxx.apps.googleusercontent.com"
            data-testid="gmail-client-id-input"
          />
        </div>
        <div>
          <label className="overline block mb-1.5">
            Client Secret {data?.client_secret_masked && <span className="ml-2 text-norea-textDisabled normal-case tracking-normal">(actuel: {data.client_secret_masked})</span>}
          </label>
          <input
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            className="input-field font-mono text-xs"
            placeholder={data?.configured ? "•••••••• (laisser vide pour conserver)" : "GOCSPX-..."}
            data-testid="gmail-client-secret-input"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={busy || !clientId} className="btn-secondary" data-testid="btn-gmail-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Sauvegarder
        </button>
        {data?.configured && !data?.connected && (
          <button onClick={authorize} disabled={busy} className="btn-primary" data-testid="btn-gmail-authorize">
            Autoriser Gmail
            <ExternalLink className="w-4 h-4" />
          </button>
        )}
        {data?.connected && (
          <>
            <button onClick={test} disabled={busy} className="btn-secondary" data-testid="btn-gmail-test">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
              Tester la connexion
            </button>
            <button onClick={disconnect} className="btn-ghost text-status-erreurText" data-testid="btn-gmail-disconnect">
              Déconnecter
            </button>
          </>
        )}
      </div>

      {testResult && (
        <div
          className={`mt-3 px-3 py-2 rounded-md text-sm ${
            testResult.ok
              ? "bg-status-completBg border border-status-completBorder text-status-completText"
              : "bg-status-erreurBg border border-status-erreurBorder text-status-erreurText"
          }`}
          data-testid="gmail-test-result"
        >
          {testResult.ok ? `✓ Connexion OK — ${testResult.email} (${testResult.total_messages} messages)` : `✗ ${testResult.error}`}
        </div>
      )}
    </div>
  );
}

// ============== CHELLY ==============
function ChellyCard({ data, onChange }) {
  const [baseUrl, setBaseUrl] = useState("");
  const [sender, setSender] = useState("");
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  React.useEffect(() => {
    // Pre-fill on mount with current saved values
    if (data?.base_url && !baseUrl) setBaseUrl(data.base_url);
    if (data?.sender && !sender) setSender(data.sender);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.base_url, data?.sender]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/connections/chelly/save", {
        base_url: baseUrl.trim(),
        sender: sender.trim(),
      });
      onChange();
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data: r } = await api.post("/connections/chelly/test");
      setTestResult(r);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-flat p-6" data-testid="card-chelly">
      <ConnectionHeader
        icon={Users}
        title="Chelly (CRM)"
        connected={data?.connected}
        configured={data?.configured}
        lastTest={data?.last_test_at}
        testid="chelly"
      />

      <p className="text-sm text-norea-textMuted mb-4">
        L'agent créera automatiquement les fiches client dans Chelly avec le bon statut (EN ATTENTE CQ ou EN ATT COMPLEMENT).
      </p>

      <div className="mb-3 px-3 py-2 rounded-md bg-norea-bg border border-norea-border text-[12px] text-norea-textMuted leading-relaxed">
        <strong className="text-norea-text">Où trouver ces infos ?</strong>{" "}
        Dans Chelly : <em>Réglages → Paramètres → onglet API → URL API internes et Zapier</em>.{" "}
        <a href="https://chelly.net/doc/?insertleads" target="_blank" rel="noreferrer" className="text-norea-accent underline">
          Documentation officielle
        </a>
      </div>

      <div className="mb-3">
        <label className="overline block mb-1.5">URL API Chelly</label>
        <input
          type="text"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          className="input-field font-mono text-xs"
          placeholder="https://chelly.net/insert_lead.php?token=XXXXX"
          data-testid="chelly-base-url-input"
        />
        <div className="mt-1 text-[11px] text-norea-textMuted">
          ⚠️ Cette URL contient déjà le jeton d'authentification — copiez-la entière depuis Chelly.
        </div>
      </div>

      <div className="mb-4">
        <label className="overline block mb-1.5">
          Sender (login utilisateur Chelly) — obligatoire
        </label>
        <input
          type="text"
          value={sender}
          onChange={(e) => setSender(e.target.value)}
          className="input-field text-sm"
          placeholder="ex: admin.norea"
          data-testid="chelly-sender-input"
        />
        <div className="mt-1 text-[11px] text-norea-textMuted">
          Le login d'un utilisateur valide du CRM — c'est lui qui sera enregistré comme créateur de chaque fiche.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={busy || !baseUrl || !sender} className="btn-secondary" data-testid="btn-chelly-save">
          Sauvegarder
        </button>
        {data?.configured && (
          <button onClick={test} disabled={busy} className="btn-primary" data-testid="btn-chelly-test">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Tester la connexion
          </button>
        )}
      </div>

      {testResult && (
        <div
          className={`mt-3 px-3 py-2 rounded-md text-sm ${
            testResult.ok
              ? "bg-status-completBg border border-status-completBorder text-status-completText"
              : "bg-status-erreurBg border border-status-erreurBorder text-status-erreurText"
          }`}
          data-testid="chelly-test-result"
        >
          {testResult.ok ? `✓ ${testResult.message || "Connexion OK"}` : `✗ ${testResult.error || testResult.message}`}
        </div>
      )}
    </div>
  );
}

// ============== TRELLO ==============
function TrelloCard({ data, onChange }) {
  const [apiKey, setApiKey] = useState("");
  const [token, setToken] = useState("");
  const [boardId, setBoardId] = useState(data?.board_id || "yF4D5teE");
  const [listComplet, setListComplet] = useState(data?.list_id_complet || "");
  const [listIncomplet, setListIncomplet] = useState(data?.list_id_incomplet || "");
  const [lists, setLists] = useState([]);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showHelp, setShowHelp] = useState(!data?.configured);

  useEffect(() => {
    if (data?.connected) {
      api.get("/connections/trello/lists").then((r) => setLists(r.data.lists || [])).catch(() => {});
    }
  }, [data?.connected]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/connections/trello/save", {
        api_key: apiKey.trim() || "__keep__",
        token: token.trim() || "__keep__",
        board_id: boardId.trim(),
        list_id_complet: listComplet,
        list_id_incomplet: listIncomplet,
      });
      setApiKey("");
      setToken("");
      onChange();
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data: r } = await api.post("/connections/trello/test");
      setTestResult(r);
      if (r.ok) {
        const { data: ll } = await api.get("/connections/trello/lists");
        setLists(ll.lists || []);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-flat p-6" data-testid="card-trello">
      <ConnectionHeader
        icon={Trello}
        title="Trello"
        connected={data?.connected}
        configured={data?.configured}
        lastTest={data?.last_test_at}
        testid="trello"
      />

      <button
        onClick={() => setShowHelp(!showHelp)}
        className="text-xs text-norea-accent hover:text-norea-primary mb-3"
        data-testid="btn-toggle-trello-help"
      >
        {showHelp ? "▼" : "▶"} Comment obtenir votre clé Trello ?
      </button>
      {showHelp && (
        <div className="mb-4 p-4 bg-norea-bg border border-norea-border rounded-md text-xs space-y-1.5 text-norea-textMuted">
          <p className="font-medium text-norea-text mb-2">📋 Étapes :</p>
          <ol className="list-decimal list-inside space-y-1.5">
            <li>
              Allez sur{" "}
              <a href="https://trello.com/power-ups/admin/" target="_blank" rel="noreferrer" className="text-norea-accent underline">
                trello.com/power-ups/admin
              </a>
            </li>
            <li>Créez un nouveau "Power-Up" et activez "API Key" → copiez la clé.</li>
            <li>
              À côté de la clé, cliquez "Token" → autorisez → copiez le token généré.
            </li>
            <li>Board ID = la partie après /b/ dans l'URL de votre board Trello.</li>
          </ol>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="overline block mb-1.5">
            API Key {data?.api_key_masked && <span className="ml-2 text-norea-textDisabled normal-case tracking-normal">({data.api_key_masked})</span>}
          </label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="input-field font-mono text-xs"
            placeholder={data?.configured ? "•••••••• (laisser vide)" : "Clé API Trello"}
            data-testid="trello-key-input"
          />
        </div>
        <div>
          <label className="overline block mb-1.5">
            Token {data?.token_masked && <span className="ml-2 text-norea-textDisabled normal-case tracking-normal">({data.token_masked})</span>}
          </label>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="input-field font-mono text-xs"
            placeholder={data?.configured ? "•••••••• (laisser vide)" : "Token Trello"}
            data-testid="trello-token-input"
          />
        </div>
        <div>
          <label className="overline block mb-1.5">Board ID</label>
          <input
            type="text"
            value={boardId}
            onChange={(e) => setBoardId(e.target.value)}
            className="input-field font-mono text-xs"
            placeholder="yF4D5teE"
            data-testid="trello-board-id-input"
          />
        </div>
        <div></div>
        <div>
          <label className="overline block mb-1.5">Liste pour dossiers COMPLETS</label>
          <select value={listComplet} onChange={(e) => setListComplet(e.target.value)} className="input-field" data-testid="trello-list-complet-select">
            <option value="">— Choisir —</option>
            {lists.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline block mb-1.5">Liste pour dossiers INCOMPLETS</label>
          <select value={listIncomplet} onChange={(e) => setListIncomplet(e.target.value)} className="input-field" data-testid="trello-list-incomplet-select">
            <option value="">— Choisir —</option>
            {lists.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={busy} className="btn-secondary" data-testid="btn-trello-save">
          Sauvegarder
        </button>
        {data?.configured && (
          <button onClick={test} disabled={busy} className="btn-primary" data-testid="btn-trello-test">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Tester
          </button>
        )}
      </div>

      {testResult && (
        <div
          className={`mt-3 px-3 py-2 rounded-md text-sm ${
            testResult.ok
              ? "bg-status-completBg border border-status-completBorder text-status-completText"
              : "bg-status-erreurBg border border-status-erreurBorder text-status-erreurText"
          }`}
          data-testid="trello-test-result"
        >
          {testResult.ok ? `✓ Board: ${testResult.board_name}` : `✗ ${testResult.error}`}
        </div>
      )}
    </div>
  );
}


// ============== RINGOVER CARD ==============
function RingoverCard({ data, onChange }) {
  const [key, setKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [defaultFrom, setDefaultFrom] = useState("");
  const [savingFrom, setSavingFrom] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (data?.configured) {
      api.get("/ringover/settings").then((r) => setDefaultFrom(r.data?.default_from_number || "")).catch(() => {});
      api.get("/ringover/from-numbers/suggest").then((r) => setSuggestions(r.data?.numbers || [])).catch(() => {});
    }
  }, [data?.configured]);

  const save = async () => {
    if (!key.trim() && !data?.configured) {
      alert("Veuillez saisir votre clé API Ringover");
      return;
    }
    setBusy(true);
    try {
      await api.post("/connections/ringover/save", {
        api_key: key.trim() || "__keep__",
      });
      setKey("");
      onChange();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data: r } = await api.post("/connections/ringover/test");
      setTestResult(r);
      if (r.ok) onChange();
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Supprimer la clé Ringover ?")) return;
    setBusy(true);
    try {
      await api.post("/connections/ringover/disconnect");
      setTestResult(null);
      onChange();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-norea-border rounded-lg p-6" data-testid="ringover-card">
      <ConnectionHeader
        icon={Phone}
        title="Ringover"
        connected={data?.connected}
        configured={data?.configured}
        lastTest={data?.last_test_at}
        testid="ringover"
      />

      <p className="text-sm text-norea-textMuted mb-4">
        Téléphonie professionnelle : click-to-call depuis les dossiers, historique d'appels par client, transcription automatique des enregistrements.
      </p>

      {!data?.configured && (
        <div className="mb-4 p-3 bg-norea-primary/5 border border-norea-primary/20 rounded-md text-xs text-norea-text">
          <div className="font-semibold mb-1">Où trouver votre clé API ?</div>
          <ol className="list-decimal ml-4 space-y-0.5">
            <li>Connectez-vous à <a href="https://dashboard.ringover.com" target="_blank" rel="noreferrer" className="text-norea-primary hover:underline">dashboard.ringover.com</a></li>
            <li>Menu <b>Développeurs → Clés API → Ajouter une clé</b></li>
            <li>Cochez les permissions : <b>Appels (lecture + écriture)</b> et <b>Contacts (lecture + écriture)</b></li>
            <li>Copiez la clé (elle ne s'affiche qu'une fois) et collez-la ci-dessous</li>
          </ol>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-norea-textMuted uppercase tracking-wider mb-1">
            Clé API Ringover
          </label>
          <div className="relative">
            <input
              type={showKey ? "text" : "password"}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder={data?.api_key_masked || "Collez votre clé Ringover ici"}
              className="w-full px-3 py-2 pr-10 border border-norea-border rounded-md text-sm outline-none focus:border-norea-primary font-mono"
              data-testid="ringover-key-input"
            />
            <button
              type="button"
              onClick={() => setShowKey((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-norea-textMuted hover:text-norea-text"
              data-testid="ringover-toggle-key"
            >
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {data?.api_key_masked && (
            <div className="text-[11px] text-norea-textMuted mt-1">
              Clé actuelle : <span className="font-mono">{data.api_key_masked}</span> · laissez vide pour la conserver.
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={save}
            disabled={busy}
            className="btn-primary disabled:opacity-50"
            data-testid="ringover-save-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
          </button>
          <button
            onClick={test}
            disabled={busy || !data?.configured}
            className="btn-secondary disabled:opacity-50"
            data-testid="ringover-test-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tester la connexion"}
          </button>
          {data?.configured && (
            <button
              onClick={disconnect}
              disabled={busy}
              className="text-xs text-norea-textMuted hover:text-status-erreurText ml-auto"
              data-testid="ringover-disconnect-btn"
            >
              Supprimer la clé
            </button>
          )}
        </div>

        {testResult && (
          <div
            className={`mt-3 px-3 py-2 rounded-md text-sm ${
              testResult.ok
                ? "bg-status-completBg border border-status-completBorder text-status-completText"
                : "bg-status-erreurBg border border-status-erreurBorder text-status-erreurText"
            }`}
            data-testid="ringover-test-result"
          >
            {testResult.ok ? `✓ ${testResult.message}` : `✗ ${testResult.error}`}
          </div>
        )}

        {/* Default from-number for click-to-call */}
        {data?.configured && (
          <div className="mt-4 pt-4 border-t border-norea-border">
            <label className="block text-xs font-medium text-norea-textMuted uppercase tracking-wider mb-1">
              Numéro appelant par défaut
            </label>
            <div className="flex items-center gap-2">
              <input
                type="tel"
                value={defaultFrom}
                onChange={(e) => setDefaultFrom(e.target.value)}
                placeholder="+33 7 55 54 56 46"
                className="flex-1 px-3 py-2 border border-norea-border rounded-md text-sm outline-none focus:border-norea-primary font-mono"
                data-testid="ringover-default-from-input"
              />
              <button
                onClick={async () => {
                  setSavingFrom(true);
                  try {
                    const r = await api.post("/ringover/settings/default-from", { default_from_number: defaultFrom });
                    setDefaultFrom(r.data.default_from_number);
                  } catch (e) {
                    alert(e?.response?.data?.detail || "Erreur");
                  } finally {
                    setSavingFrom(false);
                  }
                }}
                disabled={savingFrom || !defaultFrom.trim()}
                className="btn-secondary disabled:opacity-50"
                data-testid="ringover-default-from-save"
              >
                {savingFrom ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
              </button>
            </div>
            {suggestions.length > 0 && (
              <div className="mt-2">
                <div className="text-[11px] text-norea-textMuted mb-1">Vos lignes Ringover détectées (cliquez pour utiliser) :</div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((s) => (
                    <button
                      key={s.number}
                      onClick={() => setDefaultFrom("+" + s.number)}
                      className="text-[11px] px-2 py-1 rounded border border-norea-primary/30 bg-norea-primary/5 text-norea-primary hover:bg-norea-primary hover:text-white transition font-mono"
                      data-testid={`ringover-suggest-${s.number}`}
                    >
                      +{s.number} <span className="opacity-60">({s.count} appels)</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="text-[11px] text-norea-textMuted mt-2">
              📞 Lors d'un clic « Appeler », Ringover fera d'abord sonner ce numéro. Vous répondez, il compose ensuite le client. Vous devez donc avoir l'app Ringover Web/Desktop/Mobile ouverte sur ce numéro, ou un téléphone IP configuré.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}